import { SetStateAction, SyntheticEvent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { css } from "@emotion/react";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import { TRANSLATION_KEYS } from "shared/constants/translations";
import { useApi } from "shared/hooks/useApi";
import { useEmailValidation } from "shared/hooks/useEmailValidation";
import { ENDPOINTS, ROUTES } from "shared/types/enums";

import AuthContainer from "../AuthContainer";
import EnquiryText from "./EnquiryText";
import ResetSent from "./ResetSent";

const RequestReset = () => {
    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();

    const [email, setEmail] = useState("");

    const [sent, setSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        isEmailValid,
        emailError,
        setEmailError,
        clearEmailError,
        validateEmail
    } = useEmailValidation(email);

    const handleChange = useCallback(
        (e: { target: { value: SetStateAction<string> } }) => {
            setEmail(e.target.value);
            clearEmailError();
        },
        [clearEmailError]
    );

    const handleSubmit = async (e: SyntheticEvent) => {
        e.preventDefault();

        if (!isEmailValid) {
            setEmailError(TRANSLATION_KEYS.authInvalidEmailFormat);
        } else {
            const payload = { email };

            try {
                setIsLoading(true);

                await postData(ENDPOINTS.RequestResetPassword, payload);
                setIsLoading(false);
                setEmail("");
                handleRedirection();
            } catch (error) {
                setIsLoading(false);
                handleResponse(error);
            }
        }
    };

    const handleRedirection = useCallback(() => setSent(!sent), [sent]);
    const showError = isEmailValid ? "" : emailError;

    return sent ? (
        <ResetSent redirect={handleRedirection} />
    ) : (
        <AuthContainer
            formName={t("Auth##forgot your password")}
            secondaryText={t("Auth##enter email")}
        >
            <form noValidate onSubmit={handleSubmit}>
                <InputField
                    data-testid="forgot-password-input-field"
                    size={INPUT_FIELD_SIZES.Medium}
                    id="standard-error-helper-text"
                    fullWidth
                    name="email"
                    onBlur={validateEmail}
                    errorText={showError}
                    onChange={handleChange}
                    labelLeft={t("Dialog##email")}
                    placeholder={`${t("Dialog##example")} email@fota.com`}
                    readOnly={isLoading}
                    css={css({
                        marginBottom: "16px"
                    })}
                />

                <Button
                    data-testid="resend-password-button"
                    fullWidth
                    color={BUTTON_COLORS.Primary}
                    size={BUTTON_SIZES.Normal}
                    variant={BUTTON_VARIANTS.TextOnly}
                    type="submit"
                    isLoading={isLoading}
                >
                    {t("Auth##send reset link")}
                </Button>

                <EnquiryText
                    primaryText={t("Auth##remember password")}
                    linkText={t("Auth##sign in")}
                    component={RouterLink}
                    to={ROUTES.Login}
                />
            </form>
        </AuthContainer>
    );
};

export default RequestReset;
